import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { NAV, SITE } from "../lib/site";
import { Menu, X, HandHeart } from "lucide-react";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On non-home pages, always treat as scrolled (solid bg)
  const solid = scrolled || !isHome;

  const handleNav = (item) => {
    setOpen(false);
    if (item.type === "route") {
      navigate(item.path);
      return;
    }
    // scroll type
    if (!isHome) {
      navigate("/", { state: { scrollTo: item.id } });
      // After navigation, scroll handler in HomePage will pick this up
      setTimeout(() => {
        const el = document.getElementById(item.id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    const el = document.getElementById(item.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goDonate = () => handleNav({ type: "scroll", id: "donate" });

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-200 ${
        solid
          ? "bg-[#FAF5F0] border-b border-[#E5E0DA] shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-14 sm:h-16 lg:h-20">
          {/* Logo */}
          <button
            onClick={() => {
              if (isHome) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                navigate("/");
              }
            }}
            data-testid="nav-logo"
            className="flex items-center gap-2 sm:gap-3 group"
          >
            <img
              src={SITE.logoUrl}
              alt="Alisaafaat Welfare Foundation"
              className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 rounded-md sm:rounded-lg object-cover ring-1 ring-[#E5E0DA] bg-white"
            />
            <div className="hidden md:block text-left leading-tight">
              <div
                className={`font-serif-display text-lg font-semibold tracking-tight ${
                  solid ? "text-[#111827]" : "text-white"
                }`}
              >
                Alisaafaat
              </div>
              <div
                className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${
                  solid ? "text-[#064E3B]" : "text-[#FCD34D]"
                }`}
              >
                Welfare Foundation
              </div>
            </div>
            <span
              className={`md:hidden font-serif-display text-base font-semibold ${
                solid ? "text-[#111827]" : "text-white"
              }`}
            >
              Alisaafaat
            </span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => handleNav(n)}
                data-testid={`nav-${n.id}`}
                className={`text-sm font-medium transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-[#DC2626] hover:after:w-full after:transition-all ${
                  solid
                    ? "text-[#374151] hover:text-[#064E3B]"
                    : "text-white/90 hover:text-white drop-shadow"
                }`}
              >
                {n.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={goDonate}
              data-testid="nav-donate-cta"
              className="cta-shimmer hidden sm:inline-flex items-center gap-2 rounded-full bg-[#DC2626] hover:bg-[#B91C1C] text-white px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold shadow-sm transition-all hover:scale-[1.03]"
            >
              <HandHeart size={14} strokeWidth={2.25} />
              Donate
            </button>

            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle navigation"
              className={`lg:hidden inline-flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-full border transition-colors ${
                solid
                  ? "border-[#E5E0DA] bg-white text-[#064E3B]"
                  : "border-white/30 bg-white/15 backdrop-blur-md text-white"
              }`}
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-[#FAF5F0] border-t border-[#E5E0DA] shadow-md"
        >
          <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => handleNav(n)}
                data-testid={`nav-mobile-${n.id}`}
                className="text-left px-3 py-3 rounded-lg text-[#111827] font-medium hover:bg-white text-sm"
              >
                {n.label}
              </button>
            ))}
            <button
              onClick={goDonate}
              data-testid="nav-mobile-donate"
              className="mt-2 rounded-full bg-[#DC2626] text-white px-5 py-3 text-sm font-semibold"
            >
              Donate Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
